/**
 * Javascript Model for Kunstraum Web Application
 * 
 * @author Leander Eyer <leander.eyer@elead.ch>
 */

export { model, loadModel }

import { dataEndpoint } from './config.js'

import { Artist } from './base/Artist.js'
import { WorkOfArt } from './base/WorkOfArt.js'
import { Collection } from './base/Collection.js'
import { Exhibition } from './base/Exhibition.js'
import { Timeline } from './base/Timeline.js'
import { Tag } from './base/Tag.js'
import { ImageList } from './base/ImageList.js'

import { WorkInExhibition } from './base/WorkInExhibition.js'

import { Repository } from './Repository.js'
import { ExhibitionRepository } from './ExhibitionRepository.js'
import { TimelineRepository } from './TimelineRepository.js'
import { Selection } from './base/Selection.js'

/** The Data Model */
var model = null;

/** Trigger the loading of the Model and register a callback */
function loadModel(callback) {
	let xhr = new XMLHttpRequest();
	xhr.open('GET', dataEndpoint);
	xhr.onreadystatechange = function() {
		if (xhr.readyState == 4) {
			
			// parse the JSON
			let data = JSON.parse(xhr.responseText);

			// Build the model
			model = new Model(data);

			// Initialize the App
			callback();
		}
	};
	xhr.send();
}

/**
 * The Model class that encapsulates the data of the web application
 */
class Model {

	constructor(data) {
	
		// build the dictionary
		this.dictionaries = {
			'de': {
				'main_contact': 'Kontakt',
				'main_search': 'Suche',
				'main_noresult': 'Leider wurden keine Ergebnisse zu ihrer Suche gefunden',

				'nav_start': "Startseite",
				'nav_work': "Werke",
				'nav_artist': "Künstler",
				'nav_collection': "Sammlungen",
				'nav_collection_extern': "Externe Sammlungen",
				'nav_exhibition': "Ausstellung",
				'nav_tag': "Schlagwörter",
				'nav_timeline': "Zeitstrahl", 
				'nav_selection': "Meine Auswahl",
				'nav_aboutus': "Über uns",
				'nav_link': "Links",
				'nav_sponsor': "Sponsoren",
				'nav_archiv': "Archiv",
				'nav_cultur': "Kunstvermittlung",
				'nav_game': "Spiele",

				'detail_title': 'Titel',
				'detail_artist': 'Künstler',
				'detail_origin': 'Herkunft',
				'detail_collection': 'Sammlung',
				'detail_collection_extern': "Externe Sammlung",
				'detail_location': 'Standort',
				'detail_year': 'Jahr',
				'detail_technique': 'Technik',
				'detail_size': 'Grösse',
				'detail_addselection': 'Zu Meiner Auswahl hinzufügen',
				'detail_removeselection': 'Aus Meiner Auswahl entfernen',
				'detail_next': 'Nächstes Bild anzeigen',
				'detail_prev': 'Zurück zum vorherigen Bild',
				'detail_close': 'Detailansicht schliessen',
				'detail_zoom': 'Bild vergrössern',
				'detail_unzoom': 'Schliessen',

				'artist_birth': 'Geboren',
				'artist_death': 'Verstorben',
				'artist_in': 'in', // "geboren IN Brig"
				'artist_website': 'Website', 
				'artist_sort_random': 'Die Künstler erscheinen in zufälliger Reihenfolge',
				'artist_sort_alpha': 'Die Künstler erscheinen in alphabetischer Reihenfolge',

				'tag_hint': "Klicken Sie auf Schlagwörter um die Suche zu starten",

				'selection_empty': 'Ihre Auswahl ist noch leer. Klicken Sie auf Werke in der Seite um sie zu ihrer Auswahl hinzuzufügen.',
				'selection_share': 'Diese Auswahl Teilen',
				'selection_link': 'Link Kopieren',

				'cultur_nofiles': 'Für Deutsch gibt es noch keine Unterlagen',

				'game_memory': 'Memory',
				'game_artist': 'Künstler erkennen',
				'game_technique': 'Technik erkennen',
				'game_time': 'Welches Bild ist älter?',

				'game_memory_hint': "Das Spiel besteht aus 12 Karten, in denen Paare gefunden werden müssen.",
				'game_artist_hint': "Bei diesem Spiel wird der Kunstraum der Reihe nach 10 Kunstwerke anzeigen. Für jedes Kunstwerk stehen vier Künstler zur Auswahl. Finden Sie heraus, welcher Künstler das Werk erschaffen hat.",
				'game_technique_hint': "Bei diesem Spiel wird ihnen der Kunstraum der Reihe nach 10 Kunstwerke anzeigen. Für jedes Kunstwerk stehen vier Kunsttechniken zur Auswahl. Finden Sie heraus, mit welcher Technik das Werk erschaffen wurde.",
				'game_time_hint': "Bei diesem Spiel wird der Kunstraum 10 Paare anzeigen. Bestimme für jedes Paar, welches Bild älter ist.",

				'game_start': 'Spiel starten',
				'game_play_again': 'Nochmals spielen',
				'game_task': 'Aufgabe',
				'game_task_of': 'von',
				'game_correct': 'Gratuliere! Die Antwort ist richtig!',
				'game_wrong': 'Leider ist die Antwort falsch - richtig wäre gewesen',
				'game_wrong_short': 'Leider ist die Antwort falsch',
				'game_correct_overview': 'Antworten waren richtig!',

				'game_first_older': 'Das erste Werk ist älter',
				'game_second_older': 'Das zweite Werk ist älter'

			},

			'en': {
				'main_contact': 'Contact',
				'main_search': 'Search',
				'main_noresult': 'Unfortunately there were no results for your query',

				'nav_start': "Home",
				'nav_work': "Works of art",
				'nav_artist': "Artists",
				'nav_collection': "Collections",
				'nav_collection_extern': "External Collections",
				'nav_exhibition': "Exhibitions",
				'nav_tag': "Tags",
				'nav_timeline': "Timeline", 
				'nav_selection': "My Selection",
				'nav_aboutus': "About us",
				'nav_link': "Links",
				'nav_sponsor': "Sponsors",
				'nav_archiv': "Archive",
				'nav_cultur': "Art procurance",
				'nav_game': "Games",

				'detail_title': 'Title',
				'detail_artist': 'Artist',
				'detail_origin': 'Origin',
				'detail_collection': 'Collection',
				'detail_collection_extern': "External Collection",
				'detail_location': 'Location',
				'detail_year': 'Year',
				'detail_technique': 'Technique',
				'detail_size': 'Size',
				'detail_addselection': 'Add to my selection',
				'detail_removeselection': 'Remove from my selection',
				'detail_next': 'Show the next picture',
				'detail_prev': 'Back to the previous picture',
				'detail_close': 'Close the detail view',
				'detail_zoom': 'Magnify the image',
				'detail_unzoom': 'Close',

				'artist_birth': 'Born',
				'artist_death': 'Died',
				'artist_in': 'in', // "geboren IN Brig"
				'artist_website': 'Website', 
				'artist_sort_random': 'The artists appear in random order',
				'artist_sort_alpha': 'The artists appear in alphabetical order',

				'tag_hint': "Click on tags to start your search",

				'selection_empty': 'Your selection is still empty. Click on works of art on the page to add them to your selection.',
				'selection_share': 'Share this Selection',
				'selection_link': 'Copy Link',

				'cultur_nofiles': 'There are currently no documents in english',

				'game_memory': 'Memory',
				'game_artist': 'Recognize the Artist',
				'game_technique': 'Recognize the technique',
				'game_time': 'Which painting is older?',

				'game_memory_hint': "In this game you have to find pairs among 12 playing cards.",
				'game_artist_hint': "In this game, the art room will show you 10 works of art in turn. There are four artists to choose from for each work of art. Find out which artist created the work.",
				'game_technique_hint': "In this game, the art room will show you 10 works of art in turn. There are four art techniques to choose from for each work of art. Find out what technique was used to create the work.",
				'game_time_hint': "In this game the art room will display 10 pairs. For each pair, determine which picture is older.",

				'game_start': 'Start Game',
				'game_play_again': 'Play again',
				'game_task': 'Task',
				'game_task_of': 'of',
				'game_correct': 'Congratulations! The answer was correct!',
				'game_wrong': 'Unfortunately the answer is wrong - correct would have been',
				'game_wrong_short': 'Unfortunately the answer is wrong',
				'game_correct_overview': 'answers were right!',

				'game_first_older': 'The first work of art is older ',
				'game_second_older': 'DaThe second work of art is older'

			},

			'fr': {
				'main_contact': 'Contact',
				'main_search': 'Recherche',
				'main_noresult': 'Votre recherche n’a malheureusement donné aucun résultat.',

				'nav_start': "Acceuil",
				'nav_work': "Oeuvres",
				'nav_artist': "Artistes",
				'nav_collection': "Collections",
				'nav_collection_extern': "Collections externes",
				'nav_exhibition': "Exposition",
				'nav_tag': "Mots clés",
				'nav_timeline': "Axe de temps", 
				'nav_selection': "Ma sélection",
				'nav_aboutus': "Sur nous",
				'nav_link': "Liens",
				'nav_sponsor': "Sponsors",
				'nav_archiv': "Archiv",
				'nav_cultur': "Médiation de l'art",
				'nav_game': "Jeux",
				
				'detail_title': 'Titre',
				'detail_artist': 'Artiste',
				'detail_origin': 'Provenance',
				'detail_collection': 'Collection',
				'detail_collection_extern': "Collection externe",
				'detail_location': 'Lieu',
				'detail_year': 'Année',
				'detail_technique': 'Technique',
				'detail_size': 'Dimensionss',
				'detail_addselection': 'Ajouter à ma sélection',
				'detail_removeselection': 'Supprimer de ma sélection',
				'detail_next': 'Afficher l’image suivante',
				'detail_prev': 'Retour à l’écran précédent',
				'detail_close': 'Fermer la vue détaillée',
				'detail_zoom': 'Agrandir l’image',
				'detail_unzoom': 'Fermer',

				'artist_birth': 'Né',
				'artist_death': 'Décédé',
				'artist_in': 'à', // "geboren IN Brig"
				'artist_website': 'Website', 
				'artist_sort_random': 'Les artistes apparent en ordre aléatoire',
				'artist_sort_alpha': 'Les artistes apparent en ordre alphabetique',

				'tag_hint': "Cliquez sur Mots clés pour lancer la recherche",

				'selection_empty': 'Votre sélection est encore vide. Cliquez sur des oeuvres dans la page pour les ajouter à votre sélection.',
				'selection_share': 'Partager ma sélection',
				'selection_link': 'Copier le lien',

				'cultur_nofiles': 'Il n`y a pas encore des dossiers en français',

				'game_memory': 'Memory',
				'game_artist': 'Reconnaître l\'artsite',
				'game_technique': 'Reconnaître la technique',
				'game_time': 'Quelle image est la plus ancienne?',
				
				'game_memory_hint': 'Le jeu se compose de 12 cartes dans lesquelles il faut trouver des paires',
				'game_artist_hint': "Dans ce jeu, la salle d'art vous montrera tour à tour 10 œuvres d'art. Vous avez le choix entre quatre artistes pour chaque œuvre d'art. Découvrez quel artiste a créé l’œuvre.",
				'game_technique_hint': "Dans ce jeu, la salle d'art vous montrera tour à tour 10 œuvres d'art. Vous avez le choix entre quatre techniques artistiques pour chaque œuvre d'art. Découvrez quelle technique a été utilisée pour créer l’œuvre.",
				'game_time_hint': "Dans ce jeu, la salle d'art affichera 10 paires. Pour chaque paire, déterminez quelle image est la plus ancienne.",

				'game_start': 'Démarrer jeu',
				'game_play_again': 'Rejouer',
				'game_task': 'Tâche',
				'game_task_of': 'sur',
				'game_correct': 'Toutes nos félicitations! La réponse était correcte',
				'game_wrong': 'Malheureusement la réponse est fausse. La bonne réponse serait',
				'game_wrong_short': 'Malheureusement la réponse est fausse.',
				'game_correct_overview': 'réponses étaient correctes!',

				'game_first_older': 'La première œuvre d’art est plus ancienne',
				'game_second_older': 'La deuxième œuvre d’art est plus ancienne'
			}

		};


		// active language
		this.lang = 'de';

		// should artists be shuffled
		this.shuffleArtist = true;

		// Repositories for base Entities
		this.artists = new Repository();
		this.works = new Repository();
		this.collections = new Repository();
		this.timelines = new TimelineRepository();
		this.exhibitions = new ExhibitionRepository();
		this.tags = new Repository();

		//
		// Read base entities
		//

		// artist entities
		for (let i = 0; i < data.artist.length; i++) {
			this.artists.register(new Artist(this, data.artist [i]));
		}

		// 
		for (let i = 0; i < data.workOfArt.length; i++) {
			let workOfArt = new WorkOfArt(this, data.workOfArt [i]); 
			this.works.register(workOfArt);
		}

		// collection entitites
		for (let i = 0; i < data.collection.length; i++) {
			this.collections.register(new Collection(this, data.collection [i]));
		}

		// timeline entitites
		for (let i = 0; i < data.timeline.length; i++) {
			this.timelines.register(new Timeline(this, data.timeline [i]));
		}

		// exhibition entities
		for (let i = 0; i < data.exhibition_all.length; i++) {
			this.exhibitions.register(new Exhibition(this, data.exhibition_all [i]));
		}

		// tag entities
		for (let i = 0; i < data.tag.length; i++) {
			this.tags.register(new Tag(this, data.tag [i]));
		}
		
		// read relations
		
		// link tags
		for (let i = 0; i < data.taggedWith.length; i++) {
			let taggedWith = data.taggedWith [i];
			let tag = this.tags.getByID(taggedWith.linkTag);
			if (tag == null) {
				continue;
			}
			
			if (taggedWith.linkType == 'Artist') {
				let artist = this.artists.getByID(taggedWith.linkKey);
				if (artist == null) {
					continue;
				}
				tag.addTaggedArtist(artist);
				artist.addTag(tag);
			} else if (taggedWith.linkType == 'WorkOfArt') {
				let work = this.works.getByID(taggedWith.linkKey);
				if (work == null) {
					continue;
				}
				tag.addTaggedWork(work);
				work.addTag(tag);
			} else if (taggedWith.linkType == 'Collection') {
				let collection = this.collections.getByID(taggedWith.linkKey);
				if (collection == null) {
					continue;
				}
				tag.addTaggedCollection(collection);
				collection.addTag(tag);
			} else if (taggedWith.linkType == 'Exhibition') {
				let exhibition = this.exhibitions.getByID(taggedWith.linkKey);
				if (exhibition == null) {
					continue;
				}
				tag.addTaggedExhibition(exhibition);
				exhibition.addTag(tag);
			}
		}
		
		// link works to artists
		for (let i = 0; i < data.workCreatedBy.length; i++) {
			let createdBy = data.workCreatedBy [i];
			let work = this.works.getByID(createdBy.linkWorkOfArt);
			let artist = this.artists.getByID(createdBy.linkArtist);
			if (work != null && artist != null) {
				if (work.getFlagHideInArtistList() == false) {
					artist.addWork(work);
				}
				work.addArtist(artist);
			}
		}
		
		// link works to collections
		for (let i = 0; i < data.workInCollection.length; i++) {
			let inCollection = data.workInCollection [i];
			let work = this.works.getByID(inCollection.linkWorkOfArt);
			let collection = this.collections.getByID(inCollection.linkCollection);
			if (work != null && collection != null) {
				collection.addWork(work);
				work.addCollection(collection);
			}
		}
		
		// link works to exhibitions
		for (let i = 0; i < data.workInExhibition.length; i++) {
			let inExhibition = new WorkInExhibition(this, data.workInExhibition [i]);
			let work = this.works.getByID(inExhibition.linkWorkOfArt);
			let exhibition = this.exhibitions.getByID(inExhibition.linkExhibition);
			if (work != null && exhibition != null) {
				inExhibition.setWork(work);
				inExhibition.setExhibition(exhibition);
				exhibition.addWork(inExhibition);
				work.addExhibition(inExhibition);
			}
		}
	
		// link works to timelines
		for (let i = 0; i < data.workInTimeline.length; i++) {
			let inTimeline = data.workInTimeline [i];
			let work = this.works.getByID(inTimeline.linkWorkOfArt);
			let entity = this.timelines.getByID(inTimeline.linkTimeline);
			if (work != null && entity != null) {
				entity.addWork(work);
			}
		}

		// sort the timeline and annotate it
		this.timelines.sortAndAnotate();
		
		// Simple Block Data
		
		this.blockText = {};
		for (let i = 0; i < data.blockText.length; i++) {
			this.blockText [data.blockText [i].id] = new BlockText(this, data.blockText [i]);
		}

		this.blockLink = [];
		for (let i = 0; i < data.blockLink.length; i++) {
			this.blockLink.push(new BlockLink(data.blockLink [i]));
		}

		this.blockSponsor = [];
		for (let i = 0; i < data.blockSponsor.length; i++) {
			this.blockSponsor.push(new BlockSponsor(data.blockSponsor [i]));
		}

		this.blockAboutUs = [];
		for (let i = 0; i < data.blockAboutUs.length; i++) {
			this.blockAboutUs.push(new BlockAboutUs(data.blockAboutUs [i]));
		}

		this.document = {
			"de": [],
			"fr": [],
			"en": []
		};
		for (let i = 0; i < data.document.length; i++) {
			let lang = data.document [i].lang.toLowerCase();
			this.document [lang].push(new Document(data.document [i]));
		}

		// User Selection
		this.selection = new Selection(this);
	}
	
	// Language Management

	/** Get the current active langauge code */
	getLang() {
		return this.lang;
	}
	/** Set the current active langauge code */
	setLang(lang) {
		if (lang == 'de' || lang == 'en' || lang == 'fr') {
			this.lang = lang;
		}
	}

	/** Access a Text Translation */
	getDictionary(key) {
		return this.dictionaries [this.lang][key];
	}

	// Accessors for Repositories
	
	getArtists() {
		return this.artists;
	}

	getWorks() {
		return this.works;
	}

	getCollections() {
		return this.collections;
	}

	getTimelines() {
		return this.timelines;
	}

	getExhibitions() {
		return this.exhibitions;
	}

	getTags() {
		return this.tags;
	}
	
	getSelection() {
		return this.selection;
	}
	
	getDocument() {
		return this.document [this.lang];
	}

}

//-----------------------------------------------------------------------------------------------
//	Utility Classes

/** Generic Text Entries */
class BlockText {
	
	constructor(model, data) {
		this.id = data.id;
		this.model = model;
		this.label = data.label;
		this.valueDE = data.valueDE;
		this.valueEN = data.valueEN;
		this.valueFR = data.valueFR;
	}

	getValue() {
		if (this.model.getLang() == 'en') {
			return this.valueEN;
		} else if (this.model.getLang() == 'fr') {
			return this.valueFR;
		} else {
			return this.valueDE;
		}

	}
}

/** Generic Link Entries */
function BlockLink(data) {
	this.seq = data.seq;
	this.label = data.label;
	this.link = data.link;
}

/** Generic Sponsor Entries */
function BlockSponsor(data) {
	this.seq = data.seq;
	this.label = data.label;
	this.link = data.link;
}

/** Generic AboutUs Entries */
function BlockAboutUs(data) {
	this.seq = data.seq;
	this.name = data.name;
	this.description = data.description;
	this.image = new ImageList(data.image);
}

/** Generic Document Entries */
function Document(data) {
	this.lang = data.lang;
	this.name = data.name;
	this.description = data.description;
	this.file = JSON.parse(data.file);

	this.acceptURLTerm = function() {
		return false;
	}
}